// The Overwatch relay from the browser (server/analytics.ts is the other end).
// One GET /a/v a page answers both the consent region and the visit token; the
// sink posts what consent lets through to /a/e as RelayBatch bodies. The ingest
// key lives only in the drain Lambda.

import {
  DEVICE_ID_PATTERN,
  OW_COMMON_PARAMS,
  OW_EVENTS,
  RELAY_EVENTS_PATH,
  RELAY_MAX_BYTES,
  RELAY_MAX_EVENTS,
  RELAY_VISIT_PATH,
  VISIT_TOKEN_TTL_S,
  type OwEventName,
  type ParamSpec,
  type RelayBatch,
  type RelayEvent,
  type RelayRegion,
  type RelayUtm,
} from '../../shared/visitWire.ts';
import type { AnalyticsRelay, AnalyticsSink } from './createVisits.ts';
import type { EventName, EventParams, OwRoutedName, ParamValue } from './events.ts';

/** Client parameters the wire carries beside `params`, where server/analytics.ts reads them. */
const LIFTED_UTM = { utm_source: 'source', utm_medium: 'medium', utm_campaign: 'campaign' } as const satisfies Record<string, keyof RelayUtm>;
type Lifted = 'referrer' | keyof typeof LIFTED_UTM;

type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type Assert<T extends true> = T;
type FieldsAgree = {
  [N in OwRoutedName]: N extends OwEventName ? Same<Exclude<keyof EventParams[N], Lifted>, keyof (typeof OW_EVENTS)[N]> : false;
}[OwRoutedName];
/** A type error here: events.ts and shared/visitWire.ts disagree on which events go to Overwatch, or on their fields. */
export type RelayLockstep = Assert<Same<OwRoutedName, OwEventName> extends true ? (false extends FieldsAgree ? false : true) : false>;

/** One queued event, as createAnalytics hands it to a sink. */
export interface OutgoingEvent {
  readonly name: EventName | string;
  readonly params: Readonly<Record<string, ParamValue>>;
}

const isLifted = (k: string): k is keyof typeof LIFTED_UTM => Object.hasOwn(LIFTED_UTM, k);

/**
 * The wire form of one event, or null for a name the relay does not take. Only
 * the fields the relay declares go into `params`: the client's `site` stays
 * behind, since the relay sets it from the distribution.
 */
export function toRelayEvent(
  e: OutgoingEvent,
  events: Readonly<Record<string, Readonly<Record<string, ParamSpec>>>> = OW_EVENTS,
): RelayEvent | null {
  if (!Object.hasOwn(events, e.name)) return null;
  const spec = events[e.name]!;
  const params: Record<string, ParamValue> = {};
  const utm: RelayUtm = {};
  let referrer = '';
  for (const [k, v] of Object.entries(e.params)) {
    if (k === 'referrer') referrer = typeof v === 'string' ? v : '';
    else if (isLifted(k)) { if (typeof v === 'string' && v !== '') utm[LIFTED_UTM[k]] = v; }
    else if (Object.hasOwn(spec, k) || Object.hasOwn(OW_COMMON_PARAMS, k)) params[k] = v;
  }
  return {
    name: e.name,
    params,
    ...(referrer !== '' ? { referrer } : {}),
    ...(Object.keys(utm).length > 0 ? { utm } : {}),
  } as RelayEvent;
}

const bytes = (s: string): number => new TextEncoder().encode(s).length;

/** POST /a/e bodies for `events`, each within the relay's event and byte limits; an event too big alone is left out. */
export function relayBodies(events: readonly RelayEvent[], token: string, userId?: string): string[] {
  const body = (list: RelayEvent[]): string => {
    const batch: RelayBatch = { token, ...(userId !== undefined ? { userId } : {}), events: list };
    return JSON.stringify(batch);
  };
  const out: string[] = [];
  let chunk: RelayEvent[] = [];
  for (const e of events) {
    const next = [...chunk, e];
    if (next.length <= RELAY_MAX_EVENTS && bytes(body(next)) <= RELAY_MAX_BYTES) {
      chunk = next;
      continue;
    }
    if (chunk.length > 0) out.push(body(chunk));
    chunk = bytes(body([e])) <= RELAY_MAX_BYTES ? [e] : [];
  }
  if (chunk.length > 0) out.push(body(chunk));
  return out;
}

const VISIT_KEY = 'fxc.analytics.visit';
const DEVICE_KEY = 'fxc.analytics.device';
/** A token this close to its 12 hours is not reused from session storage. */
const TOKEN_MARGIN_MS = 10 * 60_000;
/** The relay sees a POST at most this often, bar the page-hide beacon. */
const POST_GAP_MS = 30_000;
const PENDING_MAX = 2 * RELAY_MAX_EVENTS;

interface Visit {
  readonly region: RelayRegion;
  readonly token: string;
  readonly atMs: number;
}

function readVisit(): Visit | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(VISIT_KEY) ?? 'null') as Partial<Visit> | null;
    if (v === null || (v.region !== 'eu' && v.region !== 'row') || typeof v.token !== 'string' || typeof v.atMs !== 'number') return null;
    return Date.now() - v.atMs < VISIT_TOKEN_TTL_S * 1000 - TOKEN_MARGIN_MS ? { region: v.region, token: v.token, atMs: v.atMs } : null;
  } catch {
    return null;
  }
}

/** A refused token is not reused by the next page in this tab. */
function forgetVisit(): void {
  try { sessionStorage.removeItem(VISIT_KEY); } catch { /* nothing kept */ }
}

/** A counted visitor's random device id, so Overwatch can tell a returning one; none when storage is shut. */
function deviceId(): string | undefined {
  try {
    const kept = localStorage.getItem(DEVICE_KEY);
    if (kept !== null && DEVICE_ID_PATTERN.test(kept)) return kept;
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    const id = `4xciv-${Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')}`;
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return undefined;
  }
}

export interface OverwatchRelayConfig {
  /** Prefix for `/a/v` and `/a/e`; same origin when empty. */
  base?: string;
}

/** Every failure drops quietly: no retry, so nothing can loop from a frame. */
export function createOverwatchRelay(config: OverwatchRelayConfig = {}): AnalyticsRelay {
  const base = config.base ?? '';
  let visit: Promise<Visit | null> | null = null;
  let token: string | null = null;

  function ask(): Promise<Visit | null> {
    visit ??= (async (): Promise<Visit | null> => {
      const kept = readVisit();
      if (kept !== null) return kept;
      try {
        const res = await fetch(base + RELAY_VISIT_PATH, { credentials: 'same-origin', cache: 'no-store' });
        if (!res.ok) return null;
        const v = (await res.json()) as { region?: unknown; token?: unknown };
        if ((v.region !== 'eu' && v.region !== 'row') || typeof v.token !== 'string') return null;
        const got: Visit = { region: v.region, token: v.token, atMs: Date.now() };
        try { sessionStorage.setItem(VISIT_KEY, JSON.stringify(got)); } catch { /* this page only */ }
        return got;
      } catch {
        return null;
      }
    })().then((v) => {
      token = v?.token ?? null;
      return v;
    });
    return visit;
  }

  const pending: RelayEvent[] = [];
  let lastPostMs = -Infinity;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function post(tok: string, keepalive: boolean): void {
    const events = pending.splice(0);
    if (events.length === 0) return;
    lastPostMs = performance.now();
    for (const body of relayBodies(events, tok, deviceId())) {
      try {
        fetch(base + RELAY_EVENTS_PATH, {
          method: 'POST',
          body,
          keepalive,
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
        }).then((res) => { if (res.status === 401) forgetVisit(); }, () => {});
      } catch { /* dropped */ }
    }
  }

  function drain(keepalive: boolean): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    if (token !== null) {
      post(token, keepalive);
      return;
    }
    void ask().then((v) => {
      if (v !== null) post(v.token, keepalive);
      else pending.length = 0;
    });
  }

  const sink: AnalyticsSink = {
    send(events, { beacon }): void {
      for (const e of events) {
        const w = toRelayEvent(e);
        if (w !== null) pending.push(w);
      }
      if (pending.length > PENDING_MAX) pending.splice(0, pending.length - PENDING_MAX);
      if (pending.length === 0) return;
      if (beacon) {
        drain(true);
        return;
      }
      if (timer !== null) return;
      const wait = lastPostMs + POST_GAP_MS - performance.now();
      if (wait <= 0) drain(false);
      else timer = setTimeout(() => drain(false), wait);
    },
    stop(): void {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pending.length = 0;
      try { localStorage.removeItem(DEVICE_KEY); } catch { /* nothing kept */ }
    },
  };

  return {
    region: () => ask().then((v) => v?.region ?? null),
    sink,
  };
}
